//#region Imports

import { Injectable } from '@angular/core';
import { TrackInteractor } from '../../interactors/track/track.interactor';
import { TrackCategoryProxy } from '../../models/proxies/track-category.proxy';
import { TrackProxy } from '../../models/proxies/track.proxy';
import { getErrorMessage } from '../../utils/functions';
import { RatingPayload } from '../../models/payloads/rating.payload';
import { GetManyTrackRatingsProxy } from '../../models/proxies/get-many-track-ratings.proxy';

//#endregion

@Injectable({
  providedIn: 'root',
})
export class TrackService {

  //#region Constructors

  constructor(
    private readonly trackInteractor: TrackInteractor,
  ) {
  }

  //#endregion

  //#region Functions

  public async getAllTracks(categoryId: string|null, searchText: string, currentPage: number, limit: number): Promise<[TrackProxy[] | null, string, boolean]> {
    const { error, success } = await this.trackInteractor.getAllTracks(
      categoryId, searchText, currentPage, limit
    );

    if (error || !success)
      return [null, getErrorMessage(error), false];

    const tracks = Array.isArray(success) ? success : success.data;

    const canLoadMore: boolean = success.page < success.pageCount

    return [tracks, '', canLoadMore];
  }

  public async getOneTrack(id: string): Promise<TrackProxy | null> {
    const { error, success } = await this.trackInteractor.getOneTrack(id);

    if (error || !success)
      return null;

    return success;
  }

  public async getTracksByCourseIdFromTrackCourses(courseId: string): Promise<TrackProxy[]> {
    const { error, success } = await this.trackInteractor.getTrackCoursesByCourseId(courseId);
    
    if (error || !success?.data?.length) return [];
    
    const data = success.data as Array<{ trackId?: string; track_id?: string }>;
    
    const trackIds = [...new Set(data.map((tc) => tc.trackId ?? tc.track_id).filter(Boolean))];
    const tracks: TrackProxy[] = [];
    
    for (const trackId of trackIds) {
      const track = await this.getOneTrack(trackId!);
      
      if (track) tracks.push(track);
    }

    return tracks;
  }

  public async getRatingsFromTrack(trackId: string): Promise<[GetManyTrackRatingsProxy | undefined, string?]> {
    const { error, success } = await this.trackInteractor.getRatingsFromTrack(trackId);

    if (error)
      return [undefined, getErrorMessage(error)];

    return [success];
  }

  public async sendRating(ratingPayload: RatingPayload): Promise<[boolean, string?]> {
    const { error } = await this.trackInteractor.sendRating(ratingPayload);

    if (error)
      return [false, getErrorMessage(error)];

    return [true];
  }

  public async editRating(ratingPayload: RatingPayload): Promise<[boolean, string?]> {
    const { error } = await this.trackInteractor.editRating(ratingPayload);

    if (error)
      return [false, getErrorMessage(error)];

    return [true];
  }

  public async getTrackCategories(): Promise<TrackCategoryProxy[]> {
    const { error, success } = await this.trackInteractor.getTrackCategories();

    if (error || !success)
      return [];

    return success;
  }

  //#endregion

}
